import { useState, useEffect } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import {
    X, Save, Loader2,
    CircleDashed, Package, PackageCheck, Truck, CheckCircle2, AlertTriangle,
    Calendar, FileText, Hash, User, Clock, Lock,
} from 'lucide-react';
import { useAuthStore } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

interface SalesOrder {
    id: string;
    orderNumber: string;
    customerName: string;
    status: string;
    itemsCount?: number;
    shipDate?: string;
    notes?: string;
    createdAt?: string;
    updatedAt?: string;
}

const STATUS_OPTIONS = [
    { value: 'open', label: 'Open', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: CircleDashed },
    { value: 'picking', label: 'Picking', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: Package },
    { value: 'packed', label: 'Packed', color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: PackageCheck },
    { value: 'shipped', label: 'Shipped', color: 'text-cyan-700', bg: 'bg-cyan-50 border-cyan-200', icon: Truck },
    { value: 'closed', label: 'Closed', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle2 },
];

interface Props {
    orderId: string | null;
    isOpen: boolean;
    onClose: () => void;
}

export function SalesOrderDetailsDialog({ orderId, isOpen, onClose }: Props) {
    const { token, user } = useAuthStore();
    const isOperationsEntitled = user?.effectiveTier === 'operations';

    const { data, error, isLoading } = useSWR(
        isOpen && orderId ? [`/api/sales-orders/${orderId}`, token] : null,
        ([url, t]) => fetch(url, { headers: { Authorization: `Bearer ${t}` } })
            .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); }),
        { revalidateOnFocus: false }
    );

    const order: SalesOrder | null = data?.order ?? null;

    // Editable state — reset when order changes
    const [editStatus, setEditStatus] = useState('');
    const [editShipDate, setEditShipDate] = useState('');
    const [editNotes, setEditNotes] = useState('');
    const [editItemsCount, setEditItemsCount] = useState(0);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (order) {
            setEditStatus(order.status);
            setEditShipDate(order.shipDate ? new Date(order.shipDate).toISOString().split('T')[0] : '');
            setEditNotes(order.notes ?? '');
            setEditItemsCount(order.itemsCount ?? 0);
        }
    }, [order]);

    const isDirty = order && (
        editStatus !== order.status ||
        editShipDate !== (order.shipDate ? new Date(order.shipDate).toISOString().split('T')[0] : '') ||
        editNotes !== (order.notes ?? '') ||
        editItemsCount !== (order.itemsCount ?? 0)
    );

    const handleSave = async () => {
        if (!orderId || !token || !isDirty) return;
        setSaving(true);
        try {
            const res = await fetch(`/api/sales-orders/${orderId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    status: editStatus,
                    shipDate: editShipDate || null,
                    notes: editNotes,
                    itemsCount: editItemsCount,
                }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: 'Update failed' }));
                throw new Error(err.error || `HTTP ${res.status}`);
            }
            const result = await res.json();
            const updatedOrder = result.order;
            toast.success('Sales order updated');

            // Optimistic: update detail cache
            globalMutate(
                [`/api/sales-orders/${orderId}`, token],
                { order: updatedOrder },
                { revalidate: false }
            );

            // Optimistic: update list cache by replacing the matching order
            globalMutate(
                ['/api/sales-orders', token],
                (current: any) => {
                    if (!current?.orders) return current;
                    return {
                        ...current,
                        orders: current.orders.map((o: any) =>
                            o.id === orderId ? { ...o, ...updatedOrder } : o
                        ),
                    };
                },
                { revalidate: true }
            );
            onClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to update');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    const fmt = (d?: string) => {
        if (!d) return '—';
        return d.substring(0, 10); // Or use standard import
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            {/* Dialog */}
            <div onClick={e => e.stopPropagation()} className="relative z-10 w-full max-w-lg mx-4 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900">
                            {isLoading ? 'Loading...' : order ? `SO: ${order.orderNumber}` : 'Sales Order'}
                        </h2>
                        {order && (
                            <p className="text-xs text-slate-500 mt-0.5">
                                {order.customerName}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
                    {isLoading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    )}

                    {error && (
                        <div className="flex flex-col items-center gap-3 py-8 text-red-500">
                            <AlertTriangle className="w-8 h-8" />
                            <p className="text-sm">Failed to load order</p>
                            <button
                                onClick={() => globalMutate([`/api/sales-orders/${orderId}`, token])}
                                className="text-xs text-primary hover:underline"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {order && !isLoading && !error && (
                        <>
                            {!isOperationsEntitled && (
                                <div
                                    data-testid="sales-order-dialog-readonly-banner"
                                    className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/25 text-xs text-slate-700 space-y-1"
                                >
                                    <div className="flex items-center gap-1.5 font-bold text-amber-600">
                                        <Lock className="w-3.5 h-3.5" />
                                        Read-Only Historical Sales Order (Starter Plan)
                                    </div>
                                    <p className="text-[11px] text-slate-600">
                                        Updating outbound sales order status, item counts, ship dates, or notes requires the Operations plan ($199/mo). Contact{' '}
                                        <a href="mailto:support@baywhiz.com" className="underline font-semibold text-amber-600">
                                            support@baywhiz.com
                                        </a>{' '}
                                        to upgrade.
                                    </p>
                                </div>
                            )}

                            {/* Read-only fields */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        <Hash className="w-3 h-3" /> Order Number
                                    </label>
                                    <p className="text-sm font-mono font-medium text-slate-900">{order.orderNumber}</p>
                                </div>
                                <div className="space-y-1">
                                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        <User className="w-3 h-3" /> Customer
                                    </label>
                                    <p className="text-sm text-slate-900">{order.customerName}</p>
                                </div>
                                <div className="space-y-1">
                                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        <Package className="w-3 h-3" /> Items
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        step={1}
                                        disabled={!isOperationsEntitled}
                                        value={editItemsCount}
                                        onChange={e => setEditItemsCount(Math.max(0, Math.round(Number(e.target.value) || 0)))}
                                        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-primary/50 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        <Clock className="w-3 h-3" /> Created
                                    </label>
                                    <p className="text-sm text-slate-900">{fmt(order.createdAt)}</p>
                                </div>
                            </div>

                            <hr className="border-slate-200" />

                            {/* Editable fields */}
                            <div className="space-y-4">
                                {/* Status */}
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Status</label>
                                    <div className="flex gap-1.5 flex-wrap">
                                        {STATUS_OPTIONS.map(s => {
                                            const Icon = s.icon;
                                            const active = editStatus === s.value;
                                            return (
                                                <button
                                                    key={s.value}
                                                    type="button"
                                                    disabled={!isOperationsEntitled}
                                                    onClick={() => isOperationsEntitled && setEditStatus(s.value)}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all disabled:cursor-not-allowed ${active
                                                        ? `${s.color} ${s.bg} ring-1 ring-current/30`
                                                        : 'text-slate-400 border-slate-200 hover:border-slate-300 hover:text-slate-600'
                                                        }`}
                                                >
                                                    <Icon className="w-3 h-3" />
                                                    {s.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Ship Date */}
                                <div className="space-y-1.5">
                                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        <Calendar className="w-3 h-3" /> Ship Date
                                    </label>
                                    <input
                                        type="date"
                                        disabled={!isOperationsEntitled}
                                        value={editShipDate}
                                        onChange={e => setEditShipDate(e.target.value)}
                                        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-primary/50 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                                    />
                                </div>

                                {/* Notes */}
                                <div className="space-y-1.5">
                                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        <FileText className="w-3 h-3" /> Notes
                                    </label>
                                    <textarea
                                        disabled={!isOperationsEntitled}
                                        value={editNotes}
                                        onChange={e => setEditNotes(e.target.value)}
                                        rows={3}
                                        placeholder="Add notes..."
                                        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 resize-none focus:outline-none focus:ring-1 focus:ring-primary/50 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                                    />
                                </div>
                            </div>

                            {/* Updated timestamp */}
                            {order.updatedAt && (
                                <p className="text-[10px] text-slate-400 text-right">
                                    Last updated: {fmt(order.updatedAt)}
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                {order && (
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                        >
                            {isOperationsEntitled ? 'Cancel' : 'Close'}
                        </button>
                        {isOperationsEntitled && (
                            <button
                                onClick={handleSave}
                                disabled={!isDirty || saving}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-40 hover:bg-primary/90 transition-colors"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save Changes
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
